/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSlice } from '@reduxjs/toolkit';
import { signInActions } from 'modules/sign-in/slice';
import { SLICE } from 'modules/dashboard/constants';

/**
 * Initial states of Alert widget functions are defined here
 */
//
export const initialState = {
  loading: false,
  alertData: {},
  alertParams: {},
  alertLogData: [],
  subscriptionData: {},
  alertExcel: null,
  alertRemarks: null,
  alertPdf: null,
  alertPdfError: null,
};
/**
 * All actions related to Pace Segment widget feature are defined here
 */
export const alertSlice = createSlice({
  name: SLICE.ALERT,
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    getAlertTableData(state) {
      state.loading = true;
    },
    getAlertTableDataSucceeded(state, action) {
      state.loading = false;
      state.alertData = action.payload;
    },
    getAlertTableDataFailed(state) {
      state.loading = false;
    },
    getAuditLogTableData(state) {
      state.loading = true;
    },
    getAuditLogTableDataSucceeded(state, action) {
      state.loading = false;
      state.alertLogData = action.payload;
    },
    getAuditLogTableDataFailed(state) {
      state.loading = false;
    },
    createAlert(state) {
      state.loading = true;
    },
    createAlertSucceeded(state, action) {
      state.loading = false;
      state.alertData.results = [...state.alertData.results, action?.payload];
    },
    createAlertFailed(state) {
      state.loading = false;
    },
    updateAlert(state) {
      state.loading = true;
    },
    updateAlertSucceeded(state, action) {
      state.loading = false;
      state.alertData.results = state?.alertData?.results?.map((object) =>
        object?.id === action?.payload?.id ? { ...object, ...action.payload } : object
      );
    },
    updateAlertFailed(state) {
      state.loading = false;
    },
    updateUserSubscriptions(state) {
      state.loading = true;
    },
    updateUserSubscriptionsSucceeded(state, action) {
      state.loading = false;
      const updatedSubs = action.payload;
      if (!Array.isArray(state.subscriptionData)) state.subscriptionData = [];
      //
      const getAlertId = (subscription) =>
        subscription.alertId ??
        (Array.isArray(subscription.alertIds) ? subscription.alertIds[0] : subscription.alertIds);
      const updatedMap = new Map(updatedSubs.map((sub) => [String(getAlertId(sub)), sub]));
      //
      state.subscriptionData = state.subscriptionData.map((sub) => {
        const alertId = String(getAlertId(sub));
        const updatedSub = updatedMap.get(alertId);
        //
        if (!updatedSub) return sub;
        //
        return {
          ...sub,
          ...updatedSub,
          // Preserve the grid row's ID. It is different from the subscription _id.
          id: sub.id,
        };
      });
      //
      updatedSubs.forEach((sub) => {
        const alertId = String(getAlertId(sub));
        if (
          !state.subscriptionData.some((existingSub) => String(getAlertId(existingSub)) === alertId)
        ) {
          state.subscriptionData.push(sub);
        }
      });
      // Keep alertData.toNotify in sync
      if (Array.isArray(state.alertData?.results)) {
        state.alertData.results = state.alertData.results.map((alert) =>
          updatedMap.has(String(alert.id))
            ? { ...alert, toNotify: updatedMap.get(String(alert.id)).toNotify }
            : alert
        );
      }
    },
    updateUserSubscriptionsFailed(state) {
      state.loading = false;
    },
    getSubscriptions(state) {
      state.loading = true;
    },
    getSubscriptionsSucceeded(state, action) {
      state.loading = false;
      state.subscriptionData = action.payload?.results;
    },
    getSubscriptionsFailed(state) {
      state.loading = false;
    },
    deleteAlert(state) {
      state.loading = true;
    },
    deleteAlertSucceeded(state, action) {
      state.loading = false;
      state.alertData.results = state?.alertData?.results.filter(
        (object) => object?.id !== action?.payload?.data?.alertId
      );
    },
    deleteAlertFailed(state) {
      state.loading = false;
    },
    getAlertParam(state) {
      state.loading = true;
    },
    getAlertParamSucceeded(state, action) {
      state.loading = false;
      state.alertParams = action?.payload?.results?.map((alert) => ({
        ...alert,
        alertParams: JSON.parse(alert.alertParams),
      }));
    },
    getAlertParamFailed(state) {
      state.loading = false;
    },
    getAlertExcel(state) {
      state.loading = true;
    },
    getAlertExcelSucceeded(state, action) {
      state.loading = false;
      state.alertExcel = action.payload;
    },
    getAlertExcelFailed(state) {
      state.loading = false;
    },
    getAlertPdf(state) {
      state.loading = true;
    },
    getAlertPdfSucceeded(state, action) {
      state.loading = false;
      state.alertPdf = action.payload;
    },
    getAlertPdfFailed(state, action) {
      state.loading = false;
      state.alertPdfError = action?.payload ?? true;
    },
    getAlertRemarks(state) {
      state.loading = true;
    },
    getAlertRemarksSucceeded(state, action) {
      state.loading = false;
      state.alertRemarks = action.payload;
    },
    getAlertRemarksFailed(state) {
      state.loading = false;
    },
    setAlertRemarks(state, action) {
      state.alertRemarks = action.payload;
    },
    setAlertExcel(state, action) {
      state.alertExcel = action?.payload;
    },
    setAlertPdf(state, action) {
      state.alertPdf = action?.payload;
    },
    setAlertPdfError(state, action) {
      state.alertPdfError = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signInActions.signIn, (state) => ({
      ...state,
      ...initialState,
    }));
  },
});
//
export const { actions: alertActions } = alertSlice;
